import React, { useEffect, useMemo, useState } from "react";
import BrandLogo from "../components/common/BrandLogo";
import { Helmet } from "react-helmet-async";
import { MapPin, ExternalLink, ArrowRight, ChevronRight } from "lucide-react";
import { Link } from "react-router-dom";
import { useSiteSettings } from "../lib/settings";
import { resolveImage } from "../components/admin/ImageUpload";
import Footer from "../components/layout/Footer";
import api from "../lib/api";

const SITE_ORIGIN = "https://firatconstruction.com";

const YenisehirProjectsPage = () => {
  const s = useSiteSettings();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/public/yenisehir-projects")
      .then((r) => setProjects(r.data || []))
      .catch(() => setProjects([]))
      .finally(() => setLoading(false));
  }, []);

  const baseUrl = (s.public_base_url || SITE_ORIGIN).replace(/\/$/, "");

  // JSON-LD ItemList of Places — AI/search engine friendly
  const jsonLd = useMemo(() => ({
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "ItemList",
        "name": "İstanbul Kuzeyinde Yapılan Mega Projeler — Yenişehir Bölgesi",
        "description": "Arnavutköy Yenişehir bölgesindeki TOKİ, özel sektör konut, altyapı ve karma projelerin kapsamlı rehberi.",
        "numberOfItems": projects.length,
        "itemListElement": projects.map((p, i) => ({
          "@type": "ListItem",
          "position": i + 1,
          "item": {
            "@type": "Place",
            "@id": `${baseUrl}/yenisehir-projeleri/${p.slug}`,
            "name": p.name,
            "description": p.summary || p.meta_description || "",
            "url": `${baseUrl}/yenisehir-projeleri/${p.slug}`,
            "address": { "@type": "PostalAddress", "addressLocality": p.district || "Arnavutköy", "addressRegion": p.region || "İstanbul", "addressCountry": "TR" },
            "geo": { "@type": "GeoCoordinates", "latitude": p.lat, "longitude": p.lng },
            "additionalType": p.category,
            "publisher": p.developer,
          },
        })),
      },
      {
        "@type": "BreadcrumbList",
        "itemListElement": [
          { "@type": "ListItem", "position": 1, "name": "Ana Sayfa", "item": `${baseUrl}/` },
          { "@type": "ListItem", "position": 2, "name": "İstanbul Yenişehir", "item": `${baseUrl}/yenisehir` },
          { "@type": "ListItem", "position": 3, "name": "Yenişehir Projeleri", "item": `${baseUrl}/yenisehir-projeleri` },
        ],
      },
    ],
  }), [projects, baseUrl]);

  const phone = s.real_estate_phone || s.contact_phone || "";
  const wa = (s.real_estate_whatsapp || s.whatsapp_number || "").replace(/\D/g, "");
  const waLink = wa ? `https://wa.me/${wa}?text=${encodeURIComponent("Yenişehir'de yapılan projeler hakkında bilgi almak istiyorum.")}` : "";

  return (
    <div className="yn-light bg-brand-light text-brand-navy min-h-screen" data-testid="yenisehir-projects-page">
      <Helmet>
        <html lang="tr" />
        <title>Yenişehirdeki Projeler · İstanbul Kuzeyinde Yapılan Mega Projeler Rehberi</title>
        <meta name="description" content="Arnavutköy Yenişehir bölgesindeki TOKİ, özel sektör, altyapı ve karma mega projelerin kapsamlı rehberi. Her proje için ayrı detay sayfası, GPS koordinatları, geliştirici bilgileri." />
        <meta name="keywords" content="İstanbul Yenişehir projeleri, Arnavutköy TOKİ, Yenişehirdeki projeler, Kanal İstanbul projeleri, İstanbul Kuzeyi mega projeler" />
        <link rel="canonical" href={`${baseUrl}/yenisehir-projeleri`} />
        <meta property="og:type" content="website" />
        <meta property="og:title" content="Yenişehirdeki Projeler · İstanbul Kuzeyi Mega Projeler Rehberi" />
        <meta property="og:description" content="Arnavutköy Yenişehir bölgesindeki mega projelerin GPS koordinatlı, detay sayfalı kapsamlı rehberi." />
        <meta property="og:locale" content="tr_TR" />
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>

      {/* Top bar - dark navy for contrast */}
      <header className="yn-keep-dark border-b border-brand-navy/10 bg-brand-navy sticky top-0 z-30">
        <div className="mx-auto max-w-[1400px] px-4 md:px-10 h-16 flex items-center justify-between gap-3">
          <Link to="/" aria-label="Ana sayfa" className="flex items-center flex-shrink-0"><BrandLogo place="header" onDark max={40} /></Link>
          <nav className="flex items-center gap-2 md:gap-4 text-[0.6rem] md:text-[0.66rem] tracking-[0.2em] md:tracking-[0.28em] uppercase text-white/80">
            <Link to="/yenisehir" className="hover:text-white whitespace-nowrap">Yenişehir Rehberi</Link>
            {waLink ? <a href={waLink} target="_blank" rel="noreferrer" className="border border-white/40 text-white hover:bg-white hover:text-brand-navy px-3 md:px-4 py-2 transition-colors whitespace-nowrap">WhatsApp</a> : null}
          </nav>
        </div>
      </header>

      {/* Hero */}
      <section className="py-24 md:py-32 border-b border-brand-navy/10 bg-white">
        <div className="mx-auto max-w-[1400px] px-6 md:px-10">
          <nav aria-label="breadcrumb" className="text-xs text-brand-medium flex items-center gap-2 mb-6">
            <Link to="/" className="hover:text-brand-primary">Ana Sayfa</Link>
            <ChevronRight size={12} />
            <Link to="/yenisehir" className="hover:text-brand-primary">İstanbul Yenişehir</Link>
            <ChevronRight size={12} />
            <span className="text-brand-navy">Yenişehirdeki Projeler</span>
          </nav>
          <div className="text-[0.66rem] tracking-[0.4em] uppercase text-brand-primary">İstanbul Kuzeyi · Mega Projeler</div>
          <h1 className="mt-6 font-serif leading-[1.05] tracking-tight text-brand-navy" style={{ fontSize: "clamp(2.2rem, 5.5vw, 5rem)" }}>
            Yenişehirdeki Projeler
          </h1>
          <p className="mt-8 max-w-3xl text-lg text-brand-dark/80 leading-relaxed">
            Arnavutköy Yenişehir ve çevresi; 3. Havalimanı, Kanal İstanbul ve Kuzey Marmara Otoyolu üçgeninde Türkiye&apos;nin en büyük yatırım koridorlarından biri. Aşağıda <strong className="text-brand-navy">{projects.length} mega projenin</strong> özet bilgilerini bulacaksınız — her proje için ayrı detay sayfasına giderek konum, ulaşım, özellikler ve galeri bilgilerine ulaşabilirsiniz.
          </p>
        </div>
      </section>

      {/* Projects grid */}
      <section className="py-16 md:py-24">
        <div className="mx-auto max-w-[1400px] px-6 md:px-10">
          {loading ? (
            <div className="text-brand-medium text-center py-16" data-testid="yenisehir-projects-loading">Yükleniyor…</div>
          ) : projects.length === 0 ? (
            <div className="text-brand-medium text-center py-16">Henüz proje eklenmemiş.</div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {projects.map((p, i) => {
                const gmapsUrl = p.google_maps_url || `https://www.google.com/maps?q=${p.lat},${p.lng}`;
                const heroUrl = resolveImage(p.hero_image);
                return (
                  <article
                    key={p.id || p.slug}
                    id={p.slug}
                    className="group border border-brand-navy/10 hover:border-brand-primary hover:shadow-[0_18px_60px_-30px_rgba(26,42,90,0.35)] transition-all duration-500 bg-white overflow-hidden flex flex-col"
                    data-testid={`project-${p.slug}`}
                  >
                    <Link to={`/yenisehir-projeleri/${p.slug}`} className="block relative aspect-[16/10] overflow-hidden bg-gradient-to-br from-brand-primary/10 via-white to-brand-primary/5">
                      {heroUrl ? (
                        <img src={heroUrl} alt={p.name} className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-700" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-brand-primary/25">
                          <MapPin size={40} strokeWidth={1} />
                        </div>
                      )}
                      <div className="absolute top-3 left-3 text-[0.55rem] tracking-[0.28em] uppercase text-white bg-brand-navy px-2.5 py-1.5">#{String(i + 1).padStart(2, "0")}</div>
                    </Link>
                    <div className="p-6 flex flex-col flex-1">
                      <div className="text-[0.6rem] tracking-[0.28em] uppercase text-brand-primary">{p.category}</div>
                      <h2 className="mt-3 font-serif text-xl md:text-2xl text-brand-navy leading-tight">
                        <Link to={`/yenisehir-projeleri/${p.slug}`} className="hover:text-brand-primary transition-colors">{p.name}</Link>
                      </h2>
                      <p className="mt-3 text-sm text-brand-dark/70 leading-relaxed line-clamp-3">{p.summary}</p>

                      <div className="mt-4 text-xs text-brand-medium space-y-1">
                        <div><span className="text-brand-medium/70">Geliştirici:</span> <span className="text-brand-navy">{p.developer}</span></div>
                        <div className="font-mono tabular-nums"><span className="text-brand-medium/70">GPS:</span> {Number(p.lat).toFixed(4)}, {Number(p.lng).toFixed(4)}</div>
                      </div>

                      <div className="mt-auto pt-5 flex items-center justify-between gap-2">
                        <Link
                          to={`/yenisehir-projeleri/${p.slug}`}
                          className="inline-flex items-center gap-2 text-[0.62rem] tracking-[0.24em] uppercase text-brand-primary hover:text-brand-navy transition-colors"
                          data-testid={`view-project-${p.slug}`}
                        >
                          Projeyi İncele <ArrowRight size={12} />
                        </Link>
                        <a href={gmapsUrl} target="_blank" rel="noreferrer" className="text-brand-medium hover:text-brand-primary" aria-label={`${p.name} Google Maps'te aç`}>
                          <ExternalLink size={14} />
                        </a>
                      </div>
                    </div>
                  </article>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 border-t border-brand-navy/10 bg-white">
        <div className="mx-auto max-w-3xl px-6 text-center">
          <div className="text-[0.66rem] tracking-[0.4em] uppercase text-brand-primary">Yatırım Rehberi</div>
          <h2 className="mt-6 font-serif text-3xl md:text-5xl leading-tight text-brand-navy">Bu projelerin çevresinde yatırım fırsatlarımızı inceleyin.</h2>
          <p className="mt-6 text-brand-dark/75">Uzmanlarımız her projenin bölgesine göre öngörülen değerlenme senaryolarını sizin için hazırlar.</p>
          <div className="mt-10 flex flex-wrap justify-center gap-3">
            <Link to="/yenisehir#c-09" className="inline-flex items-center gap-2 bg-brand-navy text-white px-8 py-4 text-[0.72rem] tracking-[0.3em] uppercase font-medium hover:bg-brand-primary transition-colors" data-testid="cta-danis">
              Danış &amp; Bilgi Al <ArrowRight size={14} />
            </Link>
            <Link to="/yenisehir" className="inline-flex items-center gap-2 border border-brand-navy text-brand-navy hover:bg-brand-navy hover:text-white px-8 py-4 text-[0.72rem] tracking-[0.3em] uppercase transition-colors">
              Yenişehir Rehberine Dön
            </Link>
          </div>
          {phone ? <div className="mt-8 text-sm text-brand-medium">Telefon: <a href={`tel:${phone}`} className="text-brand-primary hover:underline">{phone}</a></div> : null}
        </div>
      </section>

      <Footer settings={s} />
    </div>
  );
};

export default YenisehirProjectsPage;
